import React, { useState, useEffect, useRef } from 'react';
import { Logo } from '../constants.tsx';
import FastContactModal from './FastContactModal.tsx';
import { shareSaraCard } from './saraShareCard.ts';
import { auth } from '../firebase.ts';

const SARA_API = 'https://ai.tad-ai.com';

interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  isTyping?: boolean;
  isAnimated?: boolean;
  rating?: 'up' | 'down';
  chartUrl?: string;
}

interface SaraProfile {
  hasProfile: boolean;
  name: string | null;
  email: string | null;
  phone: string | null;
  profileType: string;
  cashReserve: number | null;
  portfolio: { code: string; name?: string; buyPrice?: number | null; quantity?: number | null }[];
  updatedAt: string | null;
}

interface SaraHistoryItem {
  id: string;
  t: string;
  q: string;
  a: string;
  stock?: string | null;
  source?: string;
}

const PROFILE_TYPE_LABELS: Record<string, string> = {
  cash_only: 'عندي سيولة وأدخّل على مراحل',
  trapped_stocks: 'عندي أسهم في خسائر',
  hybrid: 'الاتنين معاً',
  unknown: 'مش محدد',
};

const SESSIONS_KEY = 'sara_chat_sessions';
const MESSAGES_KEY_PREFIX = 'sara_chat_messages_';

const generateId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const formatDate = (timestamp: number) => {
  return new Intl.DateTimeFormat('ar-SA', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }).format(new Date(timestamp));
};

import { renderMessageText } from './renderMarkdown.tsx';

const TypewriterText: React.FC<{ text: string, onUpdate: () => void }> = ({ text, onUpdate }) => {
  const [displayedText, setDisplayedText] = useState((text || '').charAt(0));
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    let index = displayedText.length;
    if (index >= text.length) return;

    const interval = setInterval(() => {
      index += Math.floor(Math.random() * 3) + 2; 
      if (index >= textRef.current.length) {
        setDisplayedText(textRef.current);
        clearInterval(interval);
        onUpdate();
      } else {
        setDisplayedText(textRef.current.slice(0, index));
        if (index % 5 === 0) onUpdate();
      }
    }, 15);

    return () => clearInterval(interval);
  }, []);

  return <>{renderMessageText(displayedText)}</>;
};

interface SaraChatPageProps {
  currentUser?: any;
}

const SaraChatPage: React.FC<SaraChatPageProps> = ({ currentUser }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Closed by default for cleaner look
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);

  // --- Client-side view of what Sara remembers + server-side history ---
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [profile, setProfile] = useState<SaraProfile | null>(null);
  const [profileDraft, setProfileDraft] = useState<SaraProfile | null>(null);
  const [newHolding, setNewHolding] = useState({ code: '', buyPrice: '', quantity: '' });
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileNote, setProfileNote] = useState('');
  const [historyItems, setHistoryItems] = useState<SaraHistoryItem[] | null>(null);
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);

  const getIdToken = async (): Promise<string | null> => {
    // The worker charges and rate-limits by the Firebase identity, so every Sara
    // request needs the id token — the props alone are not enough.
    const user = currentUser?.auth || auth.currentUser;
    if (!user || typeof user.getIdToken !== 'function') return null;
    try {
      return await user.getIdToken();
    } catch (e) {
      return null;
    }
  };

  const saraFetch = async (path: string, options: RequestInit = {}) => {
    const token = await getIdToken();
    return fetch(`${SARA_API}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
  };

  const openProfile = async () => {
    setIsProfileOpen(true);
    setProfileNote('');
    setProfileBusy(true);
    try {
      const res = await saraFetch('/api/sara-user/profile');
      if (res.status === 401) {
        setProfileNote('لازم تسجّل الدخول الأول.');
        setIsAuthed(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setProfileDraft(data);
      }
    } catch (e) {
      setProfileNote('تعذّر تحميل الملف.');
    } finally {
      setProfileBusy(false);
    }
  };

  const openHistory = async () => {
    setIsHistoryOpen(true);
    setHistoryItems(null);
    try {
      const res = await saraFetch('/api/sara-user/history?limit=30');
      if (res.status === 401) {
        setHistoryItems([]);
        setIsAuthed(false);
        setProfileNote('لازم تسجّل الدخول الأول.');
        return;
      }
      const data = await res.json();
      setHistoryItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setHistoryItems([]);
    }
  };

  const saveProfile = async (payload: any, successText: string) => {
    setProfileBusy(true);
    setProfileNote('');
    try {
      const res = await saraFetch('/api/sara-user/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        setProfileNote(res.status === 401 ? 'لازم تسجّل الدخول الأول.' : 'تعذّر الحفظ، حاول تاني.');
        return;
      }
      const fresh = await saraFetch('/api/sara-user/profile');
      if (fresh.ok) {
        const data = await fresh.json();
        setProfile(data);
        setProfileDraft(data);
      }
      setProfileNote(successText);
    } catch (e) {
      setProfileNote('تعذّر الحفظ، حاول تاني.');
    } finally {
      setProfileBusy(false);
    }
  };

  const patchDraft = (patch: Partial<SaraProfile>) =>
    setProfileDraft(prev => (prev ? { ...prev, ...patch } : prev));

  const addDraftHolding = () => {
    const code = String(newHolding.code || '').trim();
    if (!/^\d{4}$/.test(code)) {
      setProfileNote('الكود لازم يكون 4 أرقام.');
      return;
    }
    const buyPrice = Number(newHolding.buyPrice) || null;
    const quantity = Number(newHolding.quantity) || null;
    const holding = { code, name: code, buyPrice, quantity };
    patchDraft({ portfolio: [...(profileDraft?.portfolio || []).filter(p => String(p.code) !== code), holding] });
    setNewHolding({ code: '', buyPrice: '', quantity: '' });
    setProfileNote('');
  };

  const removeDraftHolding = (code: string) =>
    patchDraft({ portfolio: (profileDraft?.portfolio || []).filter(p => String(p.code) !== code) });

  const restoreHistoryItem = (item: SaraHistoryItem) => {
    const sid = generateId();
    const words = (item.q || '').split(' ').slice(0, 5).join(' ');
    setSessions(prev => [{ id: sid, title: `${words}... - ${new Intl.DateTimeFormat('ar-SA', { month: 'short', day: 'numeric' }).format(new Date(item.t))}`, createdAt: new Date(item.t).getTime() }, ...prev]);
    setMessages([
      { id: `${sid}-q`, text: item.q, sender: 'user' },
      { id: `${sid}-a`, text: item.a, sender: 'ai', chartUrl: undefined }
    ]);
    setCurrentSessionId(sid);
    setIsHistoryOpen(false);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const questionBefore = (msgId: string): string => {
    const idx = messages.findIndex(m => m.id === msgId);
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].sender === 'user' && messages[i].text) return messages[i].text;
    }
    return '';
  };

  const handleShareCard = async (msg: Message) => {
    setSharingId(msg.id);
    const result = await shareSaraCard({ answer: msg.text, question: questionBefore(msg.id) });
    if (result === 'failed') {
      handleShareMessage(msg.text);
    }
    setSharingId(null);
  };

  // --- Market Status Pill helper ---
  const renderMarketStatusPill = () => {
    try {
      const ksaTimeStr = new Date().toLocaleString("en-US", { timeZone: "Asia/Riyadh" });
      const ksaDate = new Date(ksaTimeStr);
      const day = ksaDate.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, ..., 4 = Thursday
      const hours = ksaDate.getHours();
      const minutes = ksaDate.getMinutes();

      const isTradingDay = day >= 0 && day <= 4;
      const timeInMinutes = hours * 60 + minutes;
      const isTradingHours = timeInMinutes >= 10 * 60 && timeInMinutes < 15 * 60; // 10:00 AM to 3:00 PM
      const isOpen = isTradingDay && isTradingHours;

      if (isOpen) {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            السوق مفتوح حالياً
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-500/10 text-slate-400 border border-slate-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
            السوق مغلق
          </span>
        );
      }
    } catch (e) {
      return null;
    }
  };

  const handleShareMessage = (text: string) => {
    const cleanText = text.replace(/\[(?:SAVE_NAME|ALERT_SET|ALERT_CLEAR|SET_PORTFOLIO|PORTFOLIO_SUMMARY|SIGNAL|NOTE|TAG)[^\]]*\]/g, '').trim();
    const shareText = `تحليل سهم من سارة (TAD-AI):\n\n${cleanText}`;
    const shareUrl = `https://t.me/share/url?url=https://tad-ai.com&text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank');
  };

  // --- Quota State and Usage Fetching ---
  const [quotaStatus, setQuotaStatus] = useState<{
    hasActiveSub: boolean;
    subscription: string;
    remaining: number | null;
    total: number | null;
    todayUsage: number;
    dailyLimit: number;
    expiry: number | null;
    tokensRemaining?: number | null;
    tokensTotal?: number | null;
    tokensPerQuestion?: number | null;
    displayMode?: string;
  } | null>(null);

  const userEmail = currentUser?.profile?.email || currentUser?.auth?.email || null;
  const userRole = currentUser?.profile?.admin ? 'admin' : (currentUser?.profile?.scar ? 'scar' : 'client');
  const userTelegramId = currentUser?.profile?.telegramId || null;

  const fetchUsageStatus = async () => {
    try {
      const url = new URL(`${SARA_API}/api/sara-user/usage`);
      const userId = userEmail || currentSessionId || 'guest';
      url.searchParams.append('userId', userId);
      url.searchParams.append('role', userRole);
      url.searchParams.append('subscription', 'new user');
      if (userTelegramId) {
        url.searchParams.append('telegram_id', userTelegramId);
      }

      const res = await saraFetch(`/api/sara-user/usage?${url.searchParams.toString()}`);
      setIsAuthed(res.ok);
      if (res.ok) {
        const data = await res.json();
        setQuotaStatus(data);
      }
    } catch (e) {
      console.error("Failed to fetch usage status:", e);
    }
  };

  // Fetch usage status on mount and when sessionId/currentUser changes
  useEffect(() => {
    if (currentSessionId) {
      fetchUsageStatus();
    }
  }, [currentSessionId, currentUser]);

  const scrollToBottomIfNearBottom = () => {
    if (!chatContainerRef.current || !messagesEndRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    
    // Check if we are within 50px from the bottom
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
    
    if (isNearBottom) {
       messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Load sessions on mount
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setIsSidebarOpen(!isMobile); // Auto-open on desktop only

    const savedSessions = localStorage.getItem(SESSIONS_KEY);
    if (savedSessions) {
      try {
        const parsed = JSON.parse(savedSessions) as ChatSession[];
        setSessions(parsed.sort((a, b) => b.createdAt - a.createdAt));
        if (parsed.length > 0) {
          loadSession(parsed[0].id);
        } else {
          startNewChat();
        }
      } catch (e) {
        startNewChat();
      }
    } else {
      startNewChat();
    }
  }, []);

  // Save sessions whenever they change
  useEffect(() => {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  }, [sessions]);

  // Save messages for current session whenever they change
  useEffect(() => {
    if (currentSessionId && messages.length > 0) {
      // Don't save typing indicators
      const toSave = messages.filter(m => !m.isTyping).map(m => ({ ...m, isAnimated: false }));
      localStorage.setItem(`${MESSAGES_KEY_PREFIX}${currentSessionId}`, JSON.stringify(toSave));
    }
  }, [messages, currentSessionId]);

  // Scroll to bottom when messages list or loading state updates
  useEffect(() => {
    // Use a short timeout to ensure DOM has rendered before scrolling
    const timer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [messages, isLoading]);

  const loadSession = (sid: string) => {
    setCurrentSessionId(sid);
    const savedMessages = localStorage.getItem(`${MESSAGES_KEY_PREFIX}${sid}`);
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        setMessages([]);
      }
    } else {
      setMessages([
        { id: 'welcome', text: 'مرحباً! أنا سارة الذكاء الاصطناعي الخاص بتداول. كيف يمكنني مساعدتك في تحليل الأسهم اليوم؟', sender: 'ai' }
      ]);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const startNewChat = () => {
    const newId = generateId();
    setCurrentSessionId(newId);
    setMessages([
      { id: 'welcome', text: 'مرحباً! أنا سارة الذكاء الاصطناعي الخاص بتداول. كيف يمكنني مساعدتك في تحليل الأسهم اليوم؟', sender: 'ai' }
    ]);
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const deleteSession = (e: React.MouseEvent, sid: string) => {
    e.stopPropagation();
    const updatedSessions = sessions.filter(s => s.id !== sid);
    setSessions(updatedSessions);
    localStorage.removeItem(`${MESSAGES_KEY_PREFIX}${sid}`);
    
    if (currentSessionId === sid) {
      if (updatedSessions.length > 0) {
        loadSession(updatedSessions[0].id);
      } else {
        startNewChat();
      }
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || !currentSessionId) return;

    const userText = inputValue.trim();
    setInputValue('');

    const newUserMsg: Message = { id: Date.now().toString(), text: userText, sender: 'user' };
    const typingMsgId = 'typing-' + Date.now();
    const typingMsg: Message = { id: typingMsgId, text: '', sender: 'ai', isTyping: true };

    setMessages(prev => [...prev, newUserMsg, typingMsg]);
    setIsLoading(true);

    // Create session entry if it's the first user message
    const isFirstMessage = messages.filter(m => m.sender === 'user').length === 0;
    if (isFirstMessage) {
      // Title logic: First 5 words + Date
      const words = userText.split(' ').slice(0, 5).join(' ');
      const title = `${words}... - ${new Intl.DateTimeFormat('ar-SA', { month: 'short', day: 'numeric' }).format(new Date())}`;
      setSessions(prev => [{ id: currentSessionId, title, createdAt: Date.now() }, ...prev]);
    }

    try {
      const response = await saraFetch('/api/sara-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          session_id: currentSessionId,
          stream: true,
          email: userEmail || undefined,
          role: userRole || undefined,
          telegram_id: userTelegramId || undefined
        }),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      // Remove typing indicator before starting stream
      setMessages(prev => prev.filter(m => m.id !== typingMsgId));

      const reader = response.body?.getReader();
      // stream: true is critical for multi-byte Arabic UTF-8 — prevents incomplete
      // character sequences from being flushed mid-chunk
      const decoder = new TextDecoder("utf-8");
      const aiMsgId = Date.now().toString();
      
      // Initialize empty AI message
      setMessages(prev => [...prev, { id: aiMsgId, text: '', sender: 'ai' }]);

      let accumulatedText = '';
      // Holds the tail of the last chunk if it didn't end with \n
      // (prevents two JSON objects landing on the same "line" when TCP
      //  delivers them as one packet without a \n boundary)
      let remainder = '';
      let streamChartUrl = '';
      
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          // Prepend any incomplete line from previous chunk
          const raw = remainder + decoder.decode(value, { stream: true });
          const lines = raw.split('\n');
          // The last element may be an incomplete line — save it for next iteration
          remainder = lines.pop() ?? '';

          for (const line of lines) {
              if (!line.trim()) continue;
              try {
                  const data = JSON.parse(line);
                  if (data.token) {
                      accumulatedText += data.token;
                      setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, text: accumulatedText } : m));
                  } else if (data.replace) {
                      accumulatedText = data.replace;
                      setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, text: accumulatedText } : m));
                  } else if (data.chartUrl) {
                      streamChartUrl = data.chartUrl;
                      setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, chartUrl: streamChartUrl } : m));
                  }
              } catch (e) {
                  console.error("Error parsing stream chunk", e, "line:", line.slice(0, 120));
              }
          }
          scrollToBottomIfNearBottom();
        }

        // Flush any leftover incomplete line after stream ends
        if (remainder.trim()) {
          try {
            const data = JSON.parse(remainder);
            if (data.token) {
              accumulatedText += data.token;
            } else if (data.chartUrl) {
              streamChartUrl = data.chartUrl;
            }
          } catch (_) { /* ignore truly incomplete tail */ }
        }
        
        // Final update with complete text and chart url
        setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, text: accumulatedText, chartUrl: streamChartUrl || undefined } : m));
      }
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== typingMsgId));
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text: 'عذراً، حدث خطأ في الاتصال بالسيرفر. يرجى المحاولة لاحقاً.',
        sender: 'ai'
      }]);
    } finally {
      setIsLoading(false);
      fetchUsageStatus(); // Refresh progress bar
    }
  };

  const handleRateMessage = (id: string, rating: 'up' | 'down') => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, rating: m.rating === rating ? undefined : rating } : m));
  };

  const handleCopyMessage = (msgId: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="flex h-screen bg-[#0A1021] text-white font-sans overflow-hidden" dir="rtl">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
          .sara-chat-font { font-family: 'Cairo', sans-serif; }
          .typing-indicator span {
            display: inline-block; width: 6px; height: 6px; background-color: #8B5CF6; border-radius: 50%; margin: 0 2px;
            animation: bounce 1.4s infinite ease-in-out both;
          }
          .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
          .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
          @keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
          
          /* Custom Scrollbar for chat area */
          .chat-scrollbar::-webkit-scrollbar { width: 6px; }
          .chat-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .chat-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(139, 92, 246, 0.3); border-radius: 10px; }
          .chat-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(139, 92, 246, 0.6); }
        `}
      </style>

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div 
        className={`fixed md:static inset-y-0 right-0 z-50 w-72 bg-[#0d152b] border-l border-[#8B5CF6]/20 flex flex-col transition-transform duration-300 ease-in-out sara-chat-font
          ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
        `}
      >
        <div className="p-4 border-b border-[#8B5CF6]/20 flex flex-col gap-4">
          <a href="#/home" className="flex items-center justify-center w-full py-2 group" title="العودة للصفحة الرئيسية">
            <div className="text-[#8B5CF6] group-hover:scale-105 transition-transform flex items-center justify-center">
              <Logo />
            </div>
          </a>
          <button 
            onClick={startNewChat}
            className="w-full py-3 px-4 flex items-center justify-center gap-2 bg-[#8B5CF6]/10 hover:bg-[#8B5CF6]/20 text-[#C084FC] rounded-xl border border-[#8B5CF6]/30 transition-colors font-semibold"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
            محادثة جديدة
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={openProfile}
              className="py-2 px-3 flex items-center justify-center gap-1.5 bg-white/5 hover:bg-[#8B5CF6]/15 text-gray-300 hover:text-[#C084FC] rounded-xl border border-[#8B5CF6]/20 transition-colors text-xs font-semibold"
              title="شنو سارة فاهمة عني"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              ملفي عند سارة
            </button>
            <button
              onClick={openHistory}
              className="py-2 px-3 flex items-center justify-center gap-1.5 bg-white/5 hover:bg-[#8B5CF6]/15 text-gray-300 hover:text-[#C084FC] rounded-xl border border-[#8B5CF6]/20 transition-colors text-xs font-semibold"
              title="محادثاتاتي على السيرفر"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              محادثاتاتي
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto chat-scrollbar p-3 space-y-2">
          <p className="px-2 text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">سجل المحادثات</p>
          {sessions.length === 0 ? (
            <div className="text-center text-sm text-gray-500 mt-10">لا توجد محادثات سابقة</div>
          ) : (
            sessions.map(session => (
              <div 
                key={session.id} 
                onClick={() => loadSession(session.id)}
                className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${currentSessionId === session.id ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/30' : 'hover:bg-white/5 border border-transparent'}`}
              >
                <div className="flex flex-col overflow-hidden max-w-[85%]">
                  <span className="text-sm text-gray-200 truncate font-medium">{session.title}</span>
                  <span className="text-[10px] text-gray-500 mt-0.5">{formatDate(session.createdAt)}</span>
                </div>
                <button 
                  onClick={(e) => deleteSession(e, session.id)}
                  className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="حذف المحادثة"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                </button>
              </div>
            ))
          )}
        </div>
        <div className="p-4 border-t border-[#8B5CF6]/20 text-center">
             <p className="text-xs text-gray-500 flex items-center justify-center gap-1">سارة مدعومة من <span className="text-[#8B5CF6] font-bold">TAD-AI</span></p>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full bg-[#0A1021] relative min-w-0">
        
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-[#8B5CF6]/20 bg-[#0A1021]/80 backdrop-blur-md sticky top-0 z-30">
          <button 
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -m-2 text-gray-400 hover:text-[#8B5CF6] transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
          </button>
          <div className="flex flex-col items-center">
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#C084FC]">سارة (Sara AI)</span>
            <span className="text-[10px] text-green-500 flex items-center gap-1 mt-0.5">
              <span className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></span>
              متصل الآن
            </span>
          </div>
          <button onClick={startNewChat} className="p-2 -m-2 text-gray-400 hover:text-[#8B5CF6]">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
          </button>
        </div>

        {/* Chat Header for Desktop */}
        <div className="hidden md:flex items-center justify-between p-5 border-b border-[#8B5CF6]/10 backdrop-blur-md bg-[#0A1021]/80 sticky top-0 z-10">
           <div>
              <h2 className="text-xl font-bold text-white sara-chat-font flex items-center gap-2">
                 <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                 سارة (Sara AI)
              </h2>
              <p className="text-sm text-gray-400 mt-1">مساعدتك الذكية لتحليل الأسهم السعودية</p>
           </div>
           {/* Market Status Pill - Desktop Header */}
           <div className="flex items-center gap-3">
             {renderMarketStatusPill()}
             <a
               href="https://t.me/TAD_VIP_bot"
               target="_blank"
               rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#8B5CF6]/10 text-[#C084FC] border border-[#8B5CF6]/25 hover:bg-[#8B5CF6]/20 transition-colors"
             >
               <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/></svg>
               تيليجرام VIP
             </a>
           </div>
        </div>

        {/* ── Quota / Usage Progress Bar ── */}
        {isAuthed === false ? (
          <div className="px-4 md:px-8 py-2.5 border-b border-[#8B5CF6]/20 shrink-0 bg-[#8B5CF6]/5">
            <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-gray-300">سارة بتتحصّل على رصيدك من حسابك — سجّل الدخول عشان تشوف متبقيك وتكمّل المحادثة من أي جهاز.</span>
              <button
                onClick={() => window.dispatchEvent(new Event('tad:open-auth'))}
                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-[#8B5CF6] text-white hover:bg-[#C084FC] transition-colors"
              >
                تسجيل الدخول
              </button>
            </div>
          </div>
        ) : quotaStatus ? (
          quotaStatus.hasActiveSub && quotaStatus.total ? (() => {
            const remaining = quotaStatus.remaining || 0;
            const isCritical = remaining <= 5;
            const isWarning = remaining <= 15;
            const tpq = quotaStatus.tokensPerQuestion || 0;
            const showTokens = quotaStatus.displayMode !== 'questions' && (quotaStatus.tokensRemaining != null || tpq > 0);
            const tokensRemaining = quotaStatus.tokensRemaining ?? remaining * tpq;
            const tokensTotal = quotaStatus.tokensTotal ?? (quotaStatus.total || 0) * tpq;
            return (
              /* VIP subscriber bar */
              <div
                className="px-4 md:px-8 py-2.5 border-b shrink-0 transition-all duration-500"
                style={{
                  background: isCritical
                    ? 'linear-gradient(90deg, rgba(239,68,68,0.10), rgba(185,28,28,0.06))'
                    : isWarning
                    ? 'linear-gradient(90deg, rgba(245,158,11,0.10), rgba(180,83,9,0.06))'
                    : 'linear-gradient(90deg, rgba(139, 92, 246, 0.08), rgba(16, 185, 129, 0.06))',
                  borderColor: isCritical
                    ? 'rgba(239,68,68,0.35)'
                    : isWarning
                    ? 'rgba(245,158,11,0.30)'
                    : 'rgba(139, 92, 246, 0.25)',
                  boxShadow: isCritical
                    ? '0 0 18px rgba(239,68,68,0.25), inset 0 0 20px rgba(239,68,68,0.05)'
                    : isWarning
                    ? '0 0 12px rgba(245,158,11,0.18), inset 0 0 15px rgba(245,158,11,0.04)'
                    : 'none',
                }}
              >
                <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <svg className={`w-4 h-4 fill-current ${isCritical ? 'text-red-400 animate-pulse' : isWarning ? 'text-amber-400 animate-pulse' : 'text-yellow-400'}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-semibold text-[#E9D5FF]">
                      باقة VIP · {quotaStatus.subscription.toLowerCase().includes('week') ? 'أسبوعية' : 'شهرية'}
                    </span>
                    {isCritical && (
                      <span className="text-[10px] font-bold text-red-400 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded-full animate-pulse">
                        ⚠️ يوشك الانتهاء!
                      </span>
                    )}
                    {isWarning && !isCritical && (
                      <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
                        ⏳ على وشك النفاد
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-bold ${
                    isCritical ? 'text-red-400'
                    : isWarning ? 'text-amber-400'
                    : 'text-green-400'
                  }`}>
                    {showTokens
                      ? `${tokensRemaining.toLocaleString('en-US')} توكن متبقي`
                      : `${remaining} / ${quotaStatus.total} سؤال متبقي`}
                  </span>
                </div>
                <div className="max-w-3xl mx-auto w-full h-2 bg-slate-800/80 rounded-full overflow-hidden border border-slate-700/50">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      isCritical
                        ? 'bg-gradient-to-r from-red-500 to-red-400 animate-pulse'
                        : isWarning
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-400'
                          : 'bg-gradient-to-r from-[#8B5CF6] to-[#C084FC]'
                    }`}
                    style={{ width: `${Math.max(2, Math.min(100, (showTokens ? tokensRemaining / (tokensTotal || 1) : remaining / (quotaStatus.total || 1)) * 100))}%` }}
                  />
                </div>
                {isCritical && (
                  <p className="max-w-3xl mx-auto text-[10px] text-red-400/90 mt-1.5 text-center">
                    🔔 رصيدك على وشك الانتهاء! <a href="#/pricing" className="underline font-bold hover:text-red-300">جدد اشتراكك الآن</a>
                  </p>
                )}
              </div>
            );
          })() : (
            /* Free / no active sub bar */
            <div className="px-4 md:px-8 py-2.5 border-b border-[#8B5CF6]/20 shrink-0 bg-[#8B5CF6]/5">
              <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-[#C084FC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-xs font-semibold text-gray-400">
                    الباقة المجانية
                  </span>
                </div>
                <span className="text-xs font-bold text-[#C084FC]">
                  {quotaStatus.displayMode !== 'questions' && quotaStatus.tokensRemaining != null
                    ? `${quotaStatus.tokensRemaining.toLocaleString('en-US')} توكن متبقي اليوم`
                    : `${quotaStatus.todayUsage} / ${quotaStatus.dailyLimit} أسئلة مستخدمة اليوم`}
                </span>
              </div>
              <div className="max-w-3xl mx-auto w-full h-2 bg-slate-800/80 rounded-full overflow-hidden border border-slate-700/50">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#C084FC] transition-all duration-700"
                  style={{ width: `${Math.max(2, Math.min(100, (quotaStatus.todayUsage / Math.max(1, quotaStatus.dailyLimit)) * 100))}%` }}
                />
              </div>
              <p className="max-w-3xl mx-auto text-[10px] text-gray-400/80 mt-1 text-center">
                🚀 <a href="#/pricing" className="text-[#C084FC] hover:text-[#E9D5FF] underline font-semibold">اشترك في باقة VIP</a> للحصول على رصيد توكنات أكثر وتحليلات فنية متقدمة
              </p>
            </div>
          )
        ) : (
          /* Loading skeleton */
          <div className="px-4 md:px-8 py-2.5 border-b border-[#8B5CF6]/10 shrink-0">
            <div className="max-w-3xl mx-auto flex items-center justify-between mb-1.5">
              <div className="h-3 w-24 bg-slate-800 rounded animate-pulse" />
              <div className="h-3 w-16 bg-slate-800 rounded animate-pulse" />
            </div>
            <div className="max-w-3xl mx-auto w-full h-2 bg-slate-800 rounded-full animate-pulse" />
          </div>
        )}

        {/* ── Windows App Download Banner (Best Experience) ── */}
        <div className="px-4 md:px-8 py-2.5 bg-gradient-to-r from-[#8B5CF6]/20 via-indigo-600/15 to-emerald-600/15 border-b border-[#8B5CF6]/30 shrink-0">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0 text-right">
              <span className="text-xl shrink-0">💻</span>
              <div>
                <p className="text-xs md:text-sm font-bold text-white leading-tight">
                  لأفضل تجربة وأعلى دقة وسرعة مع الشارت اللحظي، حمّل برنامج الكمبيوتر (ويندوز)
                </p>
                <p className="text-[11px] text-gray-300 hidden sm:block mt-0.5">
                  منصة متكاملة لتحليل الأسهم، الصفقات الكبيرة، وفحص المحفظة الذاتي
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <a
                href="https://www.tad-ai.com/SARA-AI.apk"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xs md:text-sm font-bold rounded-xl shadow-lg shadow-pink-500/25 transition-all transform hover:scale-105 shrink-0"
              >
                <span>🤖</span>
                <span>تطبيق سارة (APK)</span>
              </a>
              <a
                href="https://www.tad-ai.com/TadAI-Setup.exe"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-[#8B5CF6] to-[#6366f1] hover:from-[#7C3AED] hover:to-[#4F46E5] text-white text-xs md:text-sm font-bold rounded-xl shadow-lg shadow-purple-500/25 transition-all transform hover:scale-105 shrink-0"
              >
                <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>تحميل للويندوز</span>
              </a>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto chat-scrollbar p-3 md:p-8 scroll-smooth sara-chat-font">
          <div className="max-w-3xl mx-auto space-y-4 md:space-y-6 pb-32">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className={`flex items-end gap-2 max-w-[85%] md:max-w-[75%] ${msg.sender === 'user' ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  {/* Avatar */}
                  {msg.sender === 'ai' && (
                    <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 border border-[#8B5CF6]/50 flex items-center justify-center flex-shrink-0 mb-1">
                      <svg className="w-4 h-4 text-[#C084FC]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                    </div>
                  )}
                  {msg.sender === 'user' && (
                     <div className="w-8 h-8 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center flex-shrink-0 mb-1">
                        <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                     </div>
                  )}

                  {/* Bubble */}
                  <div
                    className={`p-4 rounded-2xl text-sm md:text-base leading-relaxed ${
                      msg.sender === 'user'
                        ? 'bg-gradient-to-r from-[#8B5CF6] to-[#C084FC] text-white rounded-tl-sm shadow-[0_4px_15px_rgba(139,92,246,0.3)] font-medium'
                        : 'bg-slate-800/80 text-white rounded-tr-sm border border-slate-700/50 shadow-sm whitespace-pre-wrap'
                    }`}
                  >
                    {msg.sender === 'ai' && msg.chartUrl && (
                      <div className="mb-3 rounded-lg overflow-hidden border border-[#8B5CF6]/20 bg-[#0d152b]">
                        <img
                          src={msg.chartUrl}
                          alt="Stock Chart"
                          className="w-full h-auto"
                          loading="lazy"
                        />
                      </div>
                    )}
                    {msg.isTyping ? (
                      <div className="typing-indicator flex items-center h-6 py-1">
                        <span></span><span></span><span></span>
                      </div>
                    ) : msg.isAnimated ? (
                      <TypewriterText text={msg.text} onUpdate={scrollToBottomIfNearBottom} />
                    ) : (
                      renderMessageText(msg.text)
                    )}

                    {msg.sender === 'ai' && !msg.isTyping && (
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-700/50 text-gray-500">
                        {/* Copy Button */}
                        <button 
                          onClick={() => handleCopyMessage(msg.id, msg.text)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-all ${
                            copiedId === msg.id
                              ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                              : 'hover:bg-slate-700/60 hover:text-gray-200 border border-transparent'
                          }`}
                          title="نسخ النص"
                        >
                          {copiedId === msg.id ? (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                              <span>تم النسخ!</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                              <span>نسخ</span>
                            </>
                          )}
                        </button>

                        {/* Share Button */}
                        <button 
                          onClick={() => handleShareMessage(msg.text)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs hover:bg-blue-500/10 hover:text-blue-400 border border-transparent hover:border-blue-500/25 transition-all"
                          title="مشاركة عبر تيليجرام"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/></svg>
                          <span>مشاركة</span>
                        </button>

                        {/* Share as image card */}
                        <button
                          onClick={() => handleShareCard(msg)}
                          disabled={sharingId === msg.id}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs hover:bg-purple-500/10 hover:text-[#C084FC] border border-transparent hover:border-[#8B5CF6]/25 transition-all disabled:opacity-50"
                          title="صدّرها كصورة جاهزة للنشر"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4-4a2 2 0 013 0l4 4m-2-2l1.5-1.5a2 2 0 013 0L20 16M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"></path></svg>
                          <span>{sharingId === msg.id ? 'جاري التجهيز…' : 'كارت صورة'}</span>
                        </button>

                        {/* Divider */}
                        <div className="flex-1" />

                        {/* Rate Up */}
                        <button 
                          onClick={() => handleRateMessage(msg.id, 'up')}
                          className={`p-1.5 rounded-lg transition-all ${
                            msg.rating === 'up'
                              ? 'text-green-400 bg-green-500/10 border border-green-500/20'
                              : 'hover:text-green-400 hover:bg-green-500/10 border border-transparent'
                          }`}
                          title="مفيد"
                        >
                          <svg className="w-4 h-4" fill={msg.rating === 'up' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path></svg>
                        </button>
                        {/* Rate Down */}
                        <button 
                          onClick={() => handleRateMessage(msg.id, 'down')}
                          className={`p-1.5 rounded-lg transition-all ${
                            msg.rating === 'down'
                              ? 'text-red-400 bg-red-500/10 border border-red-500/20'
                              : 'hover:text-red-400 hover:bg-red-500/10 border border-transparent'
                          }`}
                          title="غير مفيد"
                        >
                          <svg className="w-4 h-4" fill={msg.rating === 'down' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"></path></svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-[#0A1021] via-[#0A1021] to-transparent pt-12 sara-chat-font z-20">
          <div className="max-w-3xl mx-auto">
            <div className="relative group flex items-end bg-[#0d152b]/90 border border-slate-700/50 focus-within:border-[#8B5CF6] rounded-2xl p-2 transition-all shadow-[0_0_30px_rgba(0,0,0,0.4)] backdrop-blur-sm">
               <textarea
                 value={inputValue}
                 onChange={(e) => {
                    setInputValue(e.target.value.slice(0, 1000));
                    e.target.style.height = 'inherit';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
                 }}
                 onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                       e.preventDefault();
                       handleSend();
                    }
                 }}
                 placeholder="اسأل سارة عن تحليل الشاشة أو توقعات سهم معين..."
                 className="flex-1 max-h-[150px] bg-transparent text-white text-sm md:text-base outline-none p-3 resize-none chat-scrollbar placeholder-gray-500"
                 disabled={isLoading}
                 rows={1}
                 style={{ minHeight: '52px' }}
               />
               <div className="absolute bottom-4 left-4 text-[10px] text-gray-500 font-mono">
                 {inputValue.length}/1000
               </div>
               <button
                 onClick={handleSend}
                 disabled={!inputValue.trim() || isLoading}
                 className="mb-1 ml-2 flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-[#8B5CF6] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#C084FC] hover:shadow-[0_0_15px_rgba(139,92,246,0.5)]"
               >
                 <svg className="w-5 h-5 -rotate-90 transform translate-y-[1px] -translate-x-[1px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19V5m-7 7l7-7 7 7"></path></svg>
               </button>
            </div>
            <div className="text-center mt-3 text-xs text-gray-500">
               يمكن لـ سارة الذكاء الاصطناعي أن ترتكب بعض الأخطاء في التوقعات. يرجى مراجعة التحليلات.
               <button onClick={() => setIsContactModalOpen(true)} className="mr-2 text-purple-400 hover:text-purple-300 underline underline-offset-2">
                 راسلنا مباشرة
               </button>
            </div>
          </div>
        </div>

        {/* ── What Sara knows about me ── */}
        {isProfileOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setIsProfileOpen(false)}>
            <div
              className="w-full max-w-lg max-h-[85vh] overflow-y-auto chat-scrollbar rounded-2xl bg-[#0d152b] border border-[#8B5CF6]/30 p-5 sara-chat-font"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#C084FC]">ملفك عند سارة</h3>
                  <p className="text-xs text-gray-400 mt-1">دي المعلومات اللي سارة بتقرأها مع كل سؤال عشان يطلع الرد مناسب ليك. تعدّلها أو تمسحها كلها في أي وقت.</p>
                </div>
                <button onClick={() => setIsProfileOpen(false)} className="text-gray-500 hover:text-white text-xl leading-none px-1">×</button>
              </div>

              {profileBusy && !profileDraft && <p className="text-sm text-gray-400">جاري التحميل…</p>}
              {!isAuthed && profileNote && <p className="text-sm text-amber-400 mb-3">{profileNote}</p>}

              {profileDraft && (
                <div className="space-y-4">
                  <div className="text-xs text-gray-400">
                    {profileDraft.hasProfile ? 'سارة شايفة الملف ده:' : 'لسه مفيش ملف — كل اللي جواه اختيارياً.'}
                    {profileDraft.name ? ` | الاسم: ${profileDraft.name}` : ''}
                    {profileDraft.updatedAt ? ` | آخر تحديث: ${new Date(profileDraft.updatedAt).toLocaleDateString('ar-EG')}` : ''}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">وضعيتك الحالية</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {['cash_only', 'trapped_stocks', 'hybrid'].map(t => (
                        <button
                          key={t}
                          onClick={() => patchDraft({ profileType: t })}
                          className={`px-2 py-2 rounded-xl border text-[11px] font-semibold transition-colors ${
                            profileDraft.profileType === t
                              ? 'bg-[#8B5CF6]/25 border-[#8B5CF6]/60 text-[#E9D5FF]'
                              : 'bg-white/5 border-[#8B5CF6]/20 text-gray-400 hover:text-gray-200'
                          }`}
                        >
                          {PROFILE_TYPE_LABELS[t]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">السيولة المتاحة (ريال) — اختياري</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={profileDraft.cashReserve ?? ''}
                      onChange={(e) => patchDraft({ cashReserve: e.target.value === '' ? null : Number(e.target.value) })}
                      className="w-full bg-[#0A1021] border border-[#8B5CF6]/25 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]"
                      placeholder="مثال: 50000"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">أسهمك اللي سارة بتتابعها</label>
                    <div className="space-y-2 mb-2">
                      {profileDraft.portfolio.length === 0 && <p className="text-xs text-gray-500">لا توجد أسهم.</p>}
                      {profileDraft.portfolio.map((p) => (
                        <div key={p.code} className="flex items-center justify-between gap-2 bg-white/5 border border-[#8B5CF6]/15 rounded-xl px-3 py-2">
                          <div className="text-xs text-gray-200">
                            <span className="font-mono font-bold text-[#C084FC]" dir="ltr">{p.code}</span>
                            <span className="mx-2">{p.name || ''}</span>
                            {p.buyPrice ? <span className="text-gray-400">| شراء {p.buyPrice} ريال</span> : null}
                            {p.quantity ? <span className="text-gray-400">| {p.quantity} سهم</span> : null}
                          </div>
                          <button onClick={() => removeDraftHolding(String(p.code))} className="text-gray-500 hover:text-red-400 text-xs shrink-0">إزالة</button>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        value={newHolding.code}
                        onChange={(e) => setNewHolding(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                        placeholder="الكود"
                        className="w-20 bg-[#0A1021] border border-[#8B5CF6]/25 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-[#8B5CF6]"
                        dir="ltr"
                      />
                      <input
                        value={newHolding.buyPrice}
                        onChange={(e) => setNewHolding(prev => ({ ...prev, buyPrice: e.target.value }))}
                        placeholder="سعر الشراء"
                        className="w-24 bg-[#0A1021] border border-[#8B5CF6]/25 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-[#8B5CF6]"
                      />
                      <input
                        value={newHolding.quantity}
                        onChange={(e) => setNewHolding(prev => ({ ...prev, quantity: e.target.value.replace(/\D/g, '') }))}
                        placeholder="الكمية"
                        className="w-20 bg-[#0A1021] border border-[#8B5CF6]/25 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-[#8B5CF6]"
                      />
                      <button onClick={addDraftHolding} className="px-3 py-1.5 rounded-lg bg-[#8B5CF6]/20 border border-[#8B5CF6]/40 text-[#C084FC] text-xs font-bold">إضافة</button>
                    </div>
                  </div>

                  {profileNote && <p className="text-xs text-green-400">{profileNote}</p>}

                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#8B5CF6]/15">
                    <button
                      onClick={() => saveProfile({ clear: true }, 'تم مسح الملف — سارة هترد بلا معلومات شخصية.')}
                      className="text-xs text-red-400 hover:text-red-300 underline underline-offset-2"
                    >
                      امسح كل اللي أعرفه عني
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setIsProfileOpen(false)} className="px-3 py-2 rounded-xl text-xs text-gray-400 hover:text-white">إغلاق</button>
                      <button
                        onClick={() => saveProfile({
                          profileType: profileDraft.profileType,
                          cashReserve: profileDraft.cashReserve,
                          portfolio: profileDraft.portfolio,
                          remove: (profile?.portfolio || [])
                            .filter(p => !profileDraft.portfolio.some(d => String(d.code) === String(p.code)))
                            .map(p => String(p.code))
                        }, 'تم الحفظ — سارة هتستخدمه في الردود الجاية.')}
                        disabled={profileBusy}
                        className="px-4 py-2 rounded-xl text-xs font-bold bg-[#8B5CF6] text-white hover:bg-[#C084FC] disabled:opacity-50"
                      >
                        {profileBusy ? 'جاري الحفظ…' : 'حفظ'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── My Sara conversations on the server ── */}
        {isHistoryOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setIsHistoryOpen(false)}>
            <div
              className="w-full max-w-lg max-h-[85vh] overflow-y-auto chat-scrollbar rounded-2xl bg-[#0d152b] border border-[#8B5CF6]/30 p-5 sara-chat-font"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#C084FC]">محادثاتاتي السابقة</h3>
                  <p className="text-xs text-gray-400 mt-1">آخر أسئلتك لسارة محفوظة على السيرفر — بتفضل معاك لو فتحت من جهاز تاني أو مسحتم بيانات المتصفح.</p>
                </div>
                <button onClick={() => setIsHistoryOpen(false)} className="text-gray-500 hover:text-white text-xl leading-none px-1">×</button>
              </div>

              {historyItems === null ? (
                <p className="text-sm text-gray-400">جاري التحميل…</p>
              ) : historyItems.length === 0 ? (
                <p className="text-sm text-gray-500">{isAuthed ? 'لسه مفيش محادثات محفوظة.' : profileNote || 'لازم تسجّل الدخول الأول.'}</p>
              ) : (
                <div className="space-y-2">
                  {historyItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => restoreHistoryItem(item)}
                      className="w-full text-right p-3 rounded-xl bg-white/5 border border-[#8B5CF6]/15 hover:border-[#8B5CF6]/40 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-200 line-clamp-2">{item.q}</span>
                        <span className="text-[10px] text-gray-500 shrink-0">{new Date(item.t).toLocaleString('ar-EG', { day: 'numeric', month: 'short', hour: 'numeric', minute: 'numeric' })}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 line-clamp-2">{item.a}</p>
                      {item.stock && <span className="inline-block mt-1.5 text-[10px] font-mono text-[#C084FC] bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 rounded px-1.5 py-0.5" dir="ltr">{item.stock}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <FastContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      </div>
    </div>
  );
};

export default SaraChatPage;
